import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact';

interface ThemeContextValue {
  /** What the user chose, including 'system'. */
  mode: ThemeMode;
  /** What is actually on screen right now — never 'system'. */
  resolvedTheme: 'light' | 'dark';
  density: Density;
  setMode: (mode: ThemeMode) => void;
  setDensity: (density: Density) => void;
  toggleTheme: () => void;
  toggleDensity: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_KEY = 'biovet.theme';
const DENSITY_KEY = 'biovet.density';

function systemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStored<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  if (typeof window === 'undefined') return fallback;
  const stored = window.localStorage.getItem(key);
  return stored && (allowed as readonly string[]).includes(stored) ? (stored as T) : fallback;
}

export interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
  defaultDensity?: Density;
  /** Set false when the host app persists preferences on the user record. */
  persist?: boolean;
}

/**
 * Stamps `data-theme` and `data-density` on `<html>`.
 *
 * Both are attributes rather than classes because tokens.css keys off them, so
 * changing density re-lays-out every row height in the application without a
 * single component re-render.
 */
export function ThemeProvider({
  children,
  defaultMode = 'system',
  defaultDensity = 'comfortable',
  persist = true,
}: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() =>
    persist
      ? readStored(THEME_KEY, defaultMode, ['light', 'dark', 'system'] as const)
      : defaultMode,
  );
  const [density, setDensityState] = useState<Density>(() =>
    persist
      ? readStored(DENSITY_KEY, defaultDensity, ['comfortable', 'compact'] as const)
      : defaultDensity,
  );
  const [systemPref, setSystemPref] = useState<'light' | 'dark'>(systemTheme);

  // Follow the OS while the user is on 'system'.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemPref(e.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme = mode === 'system' ? systemPref : mode;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', resolvedTheme);
    root.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  const setMode = useCallback(
    (next: ThemeMode) => {
      setModeState(next);
      if (persist) window.localStorage.setItem(THEME_KEY, next);
    },
    [persist],
  );

  const setDensity = useCallback(
    (next: Density) => {
      setDensityState(next);
      if (persist) window.localStorage.setItem(DENSITY_KEY, next);
    },
    [persist],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      resolvedTheme,
      density,
      setMode,
      setDensity,
      toggleTheme: () => setMode(resolvedTheme === 'dark' ? 'light' : 'dark'),
      toggleDensity: () => setDensity(density === 'compact' ? 'comfortable' : 'compact'),
    }),
    [mode, resolvedTheme, density, setMode, setDensity],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
